import React from 'react';
import { GripVertical, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import EmailTextBlock from './blocks/EmailTextBlock';
import EmailImageBlock from './blocks/EmailImageBlock';
import EmailButtonBlock from './blocks/EmailButtonBlock';
import EmailDividerBlock from './blocks/EmailDividerBlock';
import EmailSpacerBlock from './blocks/EmailSpacerBlock';
import EmailColumnsBlock from './blocks/EmailColumnsBlock';
import EmailSocialBlock from './blocks/EmailSocialBlock';
import EmailHeaderBlock from './blocks/EmailHeaderBlock';
import EmailFooterBlock from './blocks/EmailFooterBlock';
import EmailSignatureBlock from './blocks/EmailSignatureBlock';

const RENDERERS = {
  text: EmailTextBlock,
  image: EmailImageBlock,
  button: EmailButtonBlock,
  divider: EmailDividerBlock,
  spacer: EmailSpacerBlock,
  columns: EmailColumnsBlock,
  social: EmailSocialBlock,
  header: EmailHeaderBlock,
  footer: EmailFooterBlock,
  signature: EmailSignatureBlock,
};

function SortableBlock({ block, isSelected, onSelect, onUpdateProps, onDelete, onOpenContentPicker, onSelectSignature }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.block_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const Renderer = RENDERERS[block.block_type];
  if (!Renderer) return null;

  return (
    <div ref={setNodeRef} style={style} onClick={(e) => { e.stopPropagation(); onSelect(block.block_id); }}
      className={`relative group cursor-pointer transition-all ${isSelected ? 'ring-2 ring-stone-900 ring-offset-1 rounded-sm' : 'hover:ring-1 hover:ring-stone-300 hover:ring-offset-1 rounded-sm'}`}>

      {/* Drag handle + quick delete */}
      <div className={`absolute -left-8 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <button {...attributes} {...listeners} className="p-0.5 text-stone-400 hover:text-stone-600 cursor-grab active:cursor-grabbing">
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(block.block_id); }} className="p-0.5 text-stone-300 hover:text-red-500">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Block renderer */}
      <Renderer
        props={block.props}
        onChange={(newProps) => onUpdateProps(block.block_id, newProps)}
        isSelected={isSelected}
        onOpenContentPicker={() => onOpenContentPicker(block.block_id)}
        onSelectSignature={() => onSelectSignature(block.block_id)}
      />
    </div>
  );
}

/**
 * Email canvas — centered 600px container with sortable blocks.
 */
export default function EmailBlockCanvas({ blocks, selectedBlockId, settings, onSelectBlock, onUpdateBlockProps, onDeleteBlock, onReorderBlocks, onOpenContentPicker, onSelectSignature }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex(b => b.block_id === active.id);
    const newIndex = blocks.findIndex(b => b.block_id === over.id);
    onReorderBlocks(oldIndex, newIndex);
  };

  return (
    <div className="flex-1 overflow-y-auto bg-stone-100" onClick={() => onSelectBlock(null)}>
      <div className="py-6 px-4 flex justify-center min-h-full">
        <div style={{ width: (settings?.max_width || 600) + 'px', maxWidth: '100%', backgroundColor: settings?.content_background_color || '#ffffff' }}
          className="shadow-sm rounded-sm min-h-[200px] relative">

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map(b => b.block_id)} strategy={verticalListSortingStrategy}>
              {blocks.map(block => (
                <SortableBlock
                  key={block.block_id}
                  block={block}
                  isSelected={selectedBlockId === block.block_id}
                  onSelect={onSelectBlock}
                  onUpdateProps={onUpdateBlockProps}
                  onDelete={onDeleteBlock}
                  onOpenContentPicker={onOpenContentPicker}
                  onSelectSignature={onSelectSignature}
                />
              ))}
            </SortableContext>
          </DndContext>

          {blocks.length === 0 && (
            <div className="flex items-center justify-center h-48 text-stone-300 text-sm">
              Add blocks from the left panel
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
